/**
 * Appointment Labels API (GDN-2019).
 *
 * Hand-written RTK Query endpoints against the backend label catalog routes
 * (`/v2/provider/organizations/<org_id>/label-groups` and `/labels`). Once the
 * backend is deployed + `jotdev contract sync` runs, these can be swapped for the
 * generated `@store/generated/appointment-labels` client; the hook names + shapes
 * are kept stable so consumers don't change.
 */

import { baseApi } from '@store/baseApi';

import type { Label, LabelGroup } from '../types';

interface ListResponse<T> {
  items: T[];
  total: number;
}

const PROVIDER_ORG = (orgId: string) =>
  `/v2/provider/organizations/${orgId}`;

export const appointmentLabelsApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['LabelGroup', 'Label'] })
  .injectEndpoints({
    endpoints: builder => ({
      getLabelGroups: builder.query<ListResponse<LabelGroup>, { orgId: string }>({
        query: ({ orgId }) => `${PROVIDER_ORG(orgId)}/label-groups`,
        providesTags: ['LabelGroup'],
      }),
      getLabels: builder.query<ListResponse<Label>, { orgId: string }>({
        query: ({ orgId }) => `${PROVIDER_ORG(orgId)}/labels`,
        providesTags: ['Label'],
      }),
      createLabelGroup: builder.mutation<
        LabelGroup,
        { orgId: string; name: string; display_order?: number }
      >({
        query: ({ orgId, ...body }) => ({
          url: `${PROVIDER_ORG(orgId)}/label-groups`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['LabelGroup'],
      }),
      updateLabelGroup: builder.mutation<
        LabelGroup,
        { orgId: string; labelGroupId: string; name?: string; display_order?: number }
      >({
        query: ({ orgId, labelGroupId, ...body }) => ({
          url: `${PROVIDER_ORG(orgId)}/label-groups/${labelGroupId}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['LabelGroup', 'Label'],
      }),
      deleteLabelGroup: builder.mutation<null, { orgId: string; labelGroupId: string }>({
        query: ({ orgId, labelGroupId }) => ({
          url: `${PROVIDER_ORG(orgId)}/label-groups/${labelGroupId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['LabelGroup', 'Label'],
      }),
      createLabel: builder.mutation<
        Label,
        {
          orgId: string;
          name: string;
          color?: string;
          label_group_id?: string | null;
          display_order?: number;
        }
      >({
        query: ({ orgId, ...body }) => ({
          url: `${PROVIDER_ORG(orgId)}/labels`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Label'],
      }),
      updateLabel: builder.mutation<
        Label,
        {
          orgId: string;
          labelId: string;
          name?: string;
          color?: string;
          display_order?: number;
        }
      >({
        query: ({ orgId, labelId, ...body }) => ({
          url: `${PROVIDER_ORG(orgId)}/labels/${labelId}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: ['Label'],
      }),
      moveLabel: builder.mutation<
        Label,
        { orgId: string; labelId: string; label_group_id: string | null }
      >({
        query: ({ orgId, labelId, label_group_id }) => ({
          url: `${PROVIDER_ORG(orgId)}/labels/${labelId}/group`,
          method: 'PUT',
          body: { label_group_id },
        }),
        // Moving a label between groups changes both group label-counts.
        invalidatesTags: ['Label', 'LabelGroup'],
      }),
      deleteLabel: builder.mutation<null, { orgId: string; labelId: string }>({
        query: ({ orgId, labelId }) => ({
          url: `${PROVIDER_ORG(orgId)}/labels/${labelId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Label'],
      }),
    }),
  });

export const {
  useGetLabelGroupsQuery,
  useGetLabelsQuery,
  useCreateLabelGroupMutation,
  useUpdateLabelGroupMutation,
  useDeleteLabelGroupMutation,
  useCreateLabelMutation,
  useUpdateLabelMutation,
  useMoveLabelMutation,
  useDeleteLabelMutation,
} = appointmentLabelsApi;
